import React, { useState, useEffect, useCallback } from "react";
import {
  Video,
  Plus,
  Loader2,
  X,
  Search,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { useNavigate } from "react-router-dom";
import { projectService } from "../../services/project.service";
import { authService } from "../../services/auth.service";
import { Sidebar, Header, ProjectCard, Project, UserType, ToastContainer, Pagination } from "./DashboardComponents";
import { Button } from "../../components/ui/button";
import io from 'socket.io-client';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || "http://localhost:5000";

// Main Dashboard Component
const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [user, setUser] = useState<UserType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const loadUserProfile = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (err: any) {
      console.error("Failed to load user profile:", err);
      navigate("/login");
    }
  }, [navigate]);

  // Load user profile
  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  // Handle real-time updates for storage sync
  useEffect(() => {
    const socket = io(API_BASE_URL, {
        path: '/socket.io',
        withCredentials: true,
        transports: ['websocket', 'polling']
    });

    if (user && user.id) {
        const userId = user.id;
        socket.emit('join', userId);

        socket.on('export_status', (data: any) => {
            if (data.type === 'export_status') {
                const { status } = data.data || {};
                if (status === 'completed') {
                    // Refresh profile to update storage bar
                    loadUserProfile();
                }
            }
        });
    }

    return () => {
        socket.disconnect();
    };
  }, [user, loadUserProfile]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const response = await projectService.getProjects(currentPage, 12, debouncedSearchTerm);
        setProjects(response.items);
        setTotalPages(response.pagination.pages);
        setError(null);
      } catch (err: any) {
        setError(err.message || "Failed to load projects");
        console.error("Failed to load projects:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [currentPage, debouncedSearchTerm]);

  // Handle responsive sidebar
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      setSidebarOpen(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectTitle.trim()) return;

    try {
      setCreateLoading(true);
      const newProject = await projectService.createProject({
        title: newProjectTitle.trim(),
      });

      setNewProjectTitle("");
      setIsDialogOpen(false);

      navigate(`/editor/${newProject.id}`);
    } catch (err: any) {
      console.error("Failed to create project:", err);
      setError(err.message || "Failed to create project");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleOpenProject = (projectId: string) => {
    navigate(`/editor/${projectId}`);
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      setDeleteLoading(true);
      await projectService.deleteProject(projectToDelete);
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete));
      setIsDeleteDialogOpen(false);
      setProjectToDelete(null);
      // Reload user profile to update storage usage
      loadUserProfile();
    } catch (err: any) {
      console.error("Failed to delete project:", err);
      setError(err.message || "Failed to delete project");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenDeleteDialog = (projectId: string) => {
    setProjectToDelete(projectId);
    setIsDeleteDialogOpen(true);
  };

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  if (loading && projects.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
          <p className="text-slate-500 font-medium">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 font-sans">
      <Sidebar
        isOpen={sidebarOpen}
        isMobile={isMobile}
        user={user}
        onToggle={toggleSidebar}
        activePage="dashboard"
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          isMobile={isMobile}
          onToggleSidebar={toggleSidebar}
          user={user}
          onLogout={handleLogout}
          title="My projects"
        />
        <ToastContainer />
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-6 lg:p-10 custom-scrollbar">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <p className="text-rose-600 text-sm font-medium">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-rose-400 hover:text-rose-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
              <div>
                <p className="text-slate-500 text-sm mt-1">Manage and create your video content</p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <div className="relative w-full sm:w-64 group">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                  <Input
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 h-11 transition-all"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-semibold shadow-sm transition-all hover:scale-[1.01] active:scale-[0.99] gap-2">
                      <Plus className="h-5 w-5" />
                      New Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white sm:max-w-md rounded-2xl p-0 overflow-hidden shadow-2xl border-none">
                    <div className="p-8">
                      <DialogHeader className="mb-6">
                        <DialogTitle className="text-xl font-bold text-slate-900">
                          Create New Project
                        </DialogTitle>
                        <p className="text-slate-500 text-sm mt-1">Give your new video project a name to get started.</p>
                      </DialogHeader>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label
                            htmlFor="projectTitle"
                            className="text-xs font-semibold text-slate-700 ml-1"
                          >
                            Project Name
                          </label>
                          <Input
                            id="projectTitle"
                            placeholder="My new video clip"
                            value={newProjectTitle}
                            onChange={(e) => setNewProjectTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !createLoading) {
                                handleCreateProject();
                              }
                            }}
                            disabled={createLoading}
                            className="h-12 bg-white border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500 transition-all font-medium"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 mt-8">
                        <Button
                          variant="ghost"
                          onClick={() => setIsDialogOpen(false)}
                          disabled={createLoading}
                          className="flex-1 h-12 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 font-semibold"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleCreateProject}
                          disabled={!newProjectTitle.trim() || createLoading}
                          className="flex-1 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold transition-all shadow-md shadow-indigo-100"
                        >
                          {createLoading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            "Create"
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <Dialog
              open={isDeleteDialogOpen}
              onOpenChange={setIsDeleteDialogOpen}
            >
              <DialogContent className="bg-white sm:max-w-sm rounded-2xl p-8 shadow-2xl border-none">
                <DialogHeader className="mb-6">
                  <div className="w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mb-4">
                    <AlertTriangle className="w-7 h-7" />
                  </div>
                  <DialogTitle className="text-lg font-bold text-slate-900">
                    Delete Project
                  </DialogTitle>
                  <p className="text-slate-500 text-sm mt-2">
                    Are you sure you want to delete this project? This action cannot be undone.
                  </p>
                </DialogHeader>

                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsDeleteDialogOpen(false);
                      setProjectToDelete(null);
                    }}
                    disabled={deleteLoading}
                    className="flex-1 h-11 rounded-xl text-slate-500 hover:text-slate-900 font-semibold"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteProject}
                    disabled={deleteLoading}
                    className="flex-1 h-11 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-semibold transition-all"
                  >
                    {deleteLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Delete"
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {projects.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-20 px-6 border-2 border-dashed border-slate-200 rounded-[32px] bg-white">
                <div className="text-center max-w-sm">
                  <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Video className="h-8 w-8 text-indigo-500" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    No projects yet
                  </h3>
                  <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                    Create your first project and start telling your story through video.
                  </p>
                  <Button
                    onClick={() => setIsDialogOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 px-8 font-semibold shadow-md shadow-indigo-100 transition-all hover:scale-105 active:scale-95 gap-2"
                  >
                    <Plus className="h-5 w-5" />
                    Create New Video
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {projects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onOpen={handleOpenProject}
                      onDelete={handleOpenDeleteDialog}
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
